/**
 * Channel hooks: the signed-in user's own linked channels (the account surface).
 */

import { hubQueryKeys } from "@lifter/hub-ui/browser-shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useHubApi } from "./context";

export function useMyChannelsQuery() {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.meChannels(),
    queryFn: () => api.me.channels(),
  });
}

export function useChannelAdminQuery() {
  const api = useHubApi();
  return useQuery({ queryKey: hubQueryKeys.channelAdmin(), queryFn: () => api.channels.list() });
}

function useChannelMutation<T>(mutationFn: (input: T) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: hubQueryKeys.channelAdmin() }),
  });
}

export function useInstallGoogleChatMutation() {
  const api = useHubApi();
  return useChannelMutation(
    (input: { serviceAccountJson: string; addOnServiceAccountEmail: string }) =>
      api.channels.googleChat.install(input),
  );
}

export function useSetGoogleChatEnabledMutation() {
  const api = useHubApi();
  return useChannelMutation((enabled: boolean) => api.channels.googleChat.setEnabled(enabled));
}

export function useRemoveGoogleChatMutation() {
  const api = useHubApi();
  return useChannelMutation(() => api.channels.googleChat.remove());
}
